import type { ChurchFinanceOverview, FinanceHomeOverview, MembersBoardOverview } from "@ibada/shared-types/overview"

import { subtractMoney } from "@/features/finance/lib/finance-utils"
import { ReportModule, type FinanceReportEntry } from "@/features/reports/model/report-registry-types"

/**
 * The catalog's "essentials of the month": three report cards that carry a live headline figure.
 *
 * The figure is never computed by a new report query. Each card reads one of the boards the reader already has
 * (the branch home's finance and members payloads, or the church-wide finance board in the parish), so the
 * permission and disclosure decisions (ADR-0024) are the ones those endpoints already make server-side. When a
 * figure is withheld or the read fails, the card stays and says what the report is instead.
 */

/** Which headline a key card shows, and therefore which board it reads. */
export enum KeyReportMetric {
  /** Branch: income minus expenses this month (`GET /finance/overview/home`). */
  MonthResult = "month-result",
  /** Branch: cash held today, and its movement since the 1st (`GET /finance/overview/home`). */
  CashOnHand = "cash-on-hand",
  /** Branch: this month's spending (`GET /finance/overview/home`). */
  MonthSpending = "month-spending",
  /** Branch or parish: the head count (`GET /members/overview/board`). */
  MembersCount = "members-count",
  /** Parish: the parish's own net this month (`GET /finance/overview/church`, the parish row). */
  ParishMonthResult = "parish-month-result",
  /** Parish: the cash the parish itself holds (`GET /finance/overview/church`). */
  ParishCash = "parish-cash",
  /** Parish: income minus expenses across the whole church this month (`GET /finance/overview/church`). */
  ChurchResult = "church-result",
  /** No headline: the card shows the report's description. */
  None = "none",
}

/** The board a metric reads. */
export enum KeyFigureSource {
  FinanceHome = "finance-home",
  ChurchFinance = "church-finance",
  MembersBoard = "members-board",
  None = "none",
}

export const KEY_METRIC_SOURCE: Readonly<Record<KeyReportMetric, KeyFigureSource>> = {
  [KeyReportMetric.MonthResult]: KeyFigureSource.FinanceHome,
  [KeyReportMetric.CashOnHand]: KeyFigureSource.FinanceHome,
  [KeyReportMetric.MonthSpending]: KeyFigureSource.FinanceHome,
  [KeyReportMetric.MembersCount]: KeyFigureSource.MembersBoard,
  [KeyReportMetric.ParishMonthResult]: KeyFigureSource.ChurchFinance,
  [KeyReportMetric.ParishCash]: KeyFigureSource.ChurchFinance,
  [KeyReportMetric.ChurchResult]: KeyFigureSource.ChurchFinance,
  [KeyReportMetric.None]: KeyFigureSource.None,
}

export interface KeyReportCandidate {
  reportId: string
  metric: KeyReportMetric
}

/** How many essentials the catalog shows. */
export const KEY_REPORT_LIMIT = 3

/**
 * A branch's essentials, most relevant first. The first three the reader can open are shown; the rest are the
 * stand-ins when a module is out of reach (no Members access, a report the server will not run here, ...).
 */
export const BRANCH_KEY_REPORTS: readonly KeyReportCandidate[] = [
  { reportId: "income-statement", metric: KeyReportMetric.MonthResult },
  { reportId: "cash-flow", metric: KeyReportMetric.CashOnHand },
  { reportId: "members-by-status", metric: KeyReportMetric.MembersCount },
  { reportId: "expense-by-category", metric: KeyReportMetric.MonthSpending },
  { reportId: "income-vs-expense-trend", metric: KeyReportMetric.None },
  { reportId: "balance-sheet", metric: KeyReportMetric.None },
  { reportId: "activity-participation", metric: KeyReportMetric.None },
  { reportId: "new-members-by-month", metric: KeyReportMetric.None },
]

/**
 * The parish's essentials. Its income statement and cash flow read the PARISH's own books, so their figures
 * come from the parish's own row of the church board (never the church-wide totals, which would be a different
 * statement). The church-wide result belongs to the consolidated trend report.
 */
export const PARISH_KEY_REPORTS: readonly KeyReportCandidate[] = [
  { reportId: "income-statement", metric: KeyReportMetric.ParishMonthResult },
  { reportId: "cash-flow", metric: KeyReportMetric.ParishCash },
  { reportId: "members-by-status", metric: KeyReportMetric.MembersCount },
  { reportId: "income-vs-expense-trend", metric: KeyReportMetric.ChurchResult },
  { reportId: "balance-sheet", metric: KeyReportMetric.None },
  { reportId: "activity-participation", metric: KeyReportMetric.None },
  { reportId: "new-members-by-month", metric: KeyReportMetric.None },
]

export interface KeyReportSlot {
  entry: FinanceReportEntry
  metric: KeyReportMetric
}

/**
 * The essentials this reader actually gets.
 *
 * A candidate is skipped (and the next one takes its place) when its report is not on the reader's catalog,
 * is locked or disabled, or belongs to a module the reader cannot view. Never more than `limit`.
 */
export function selectKeyReports(input: {
  candidates: readonly KeyReportCandidate[]
  available: readonly FinanceReportEntry[]
  lockedIds: ReadonlySet<string>
  canViewModule: (module: ReportModule) => boolean
  limit?: number
}): KeyReportSlot[] {
  const byId = new Map(input.available.map((entry) => [entry.id, entry]))
  const slots: KeyReportSlot[] = []
  for (const candidate of input.candidates) {
    if (slots.length >= (input.limit ?? KEY_REPORT_LIMIT)) break
    const entry = byId.get(candidate.reportId)
    if (!entry || entry.disabled || input.lockedIds.has(entry.id)) continue
    if (!input.canViewModule(entry.module)) continue
    if (slots.some((slot) => slot.entry.id === entry.id)) continue
    slots.push({ entry, metric: candidate.metric })
  }
  return slots
}

export enum KeyFigureState {
  Loading = "loading",
  Ready = "ready",
  /** The board answered, but this reader may not see the absolute figures (ADR-0024). */
  Withheld = "withheld",
  /** No headline for this card: none is defined, or the board could not be read. */
  None = "none",
}

export enum KeyFigureTone {
  Neutral = "neutral",
  Positive = "positive",
  Negative = "negative",
}

export enum MiniChartKind {
  /** Income and expense side by side, per period. */
  Pairs = "pairs",
  /** One bar per period or per row; the last one is emphasised. */
  Bars = "bars",
}

export type MiniChart =
  | { kind: MiniChartKind.Pairs; pairs: Array<{ income: number; expense: number }> }
  | { kind: MiniChartKind.Bars; values: number[] }

export enum KeyValueKind {
  Money = "money",
  Count = "count",
}

export type KeyFigureValue =
  | { kind: KeyValueKind.Money; amount: string; currency: string; signed: boolean }
  | { kind: KeyValueKind.Count; count: number }

/** The one muted line under the figure: an i18n key under `finance.reports.catalog.keys.context`. */
export enum KeyContextKey {
  IncomeAndExpenses = "incomeAndExpenses",
  SinceFirst = "sinceFirst",
  ForIncome = "forIncome",
  NewThisMonth = "newThisMonth",
  ActiveMembers = "activeMembers",
  ParishBooks = "parishBooks",
  ChurchTotal = "churchTotal",
  AllBranches = "allBranches",
}

export interface KeyFigureContext {
  key: KeyContextKey
  /** Money params, formatted in the currency of the figure. */
  money?: Record<string, { amount: string; signed: boolean }>
  counts?: Record<string, number>
}

export type KeyFigure =
  | { state: KeyFigureState.Loading }
  | { state: KeyFigureState.Withheld }
  | { state: KeyFigureState.None }
  | {
      state: KeyFigureState.Ready
      value: KeyFigureValue
      tone: KeyFigureTone
      chart: MiniChart | null
      context: KeyFigureContext | null
    }

export interface KeyQueryState<T> {
  data: T | undefined
  isLoading: boolean
  isError: boolean
}

export interface KeyFigureSources {
  financeHome: KeyQueryState<FinanceHomeOverview>
  churchFinance: KeyQueryState<ChurchFinanceOverview>
  membersBoard: KeyQueryState<MembersBoardOverview>
}

/** How many periods (weeks, months, branches) a mini chart draws. */
export const MINI_CHART_POINTS = 6

const NONE: KeyFigure = { state: KeyFigureState.None }
const LOADING: KeyFigure = { state: KeyFigureState.Loading }
const WITHHELD: KeyFigure = { state: KeyFigureState.Withheld }

const isNegativeMoney = (value: string) => value.trim().startsWith("-")
const isZeroMoney = (value: string) => Number(value) === 0
const toneOf = (value: string) =>
  isNegativeMoney(value) ? KeyFigureTone.Negative : isZeroMoney(value) ? KeyFigureTone.Neutral : KeyFigureTone.Positive
/** Chart geometry is the one place a money string becomes a number; the figure itself stays exact. */
const chartNumber = (value: string) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : 0
}

/** A board that is still loading or could not be read has no headline yet; else its data. */
function settle<T>(query: KeyQueryState<T>): { figure: KeyFigure } | { data: T } {
  if (query.isLoading) return { figure: LOADING }
  if (query.isError || !query.data) return { figure: NONE }
  return { data: query.data }
}

/** The headline for one key card, from the board it reads. Pure: the card does the wording. */
export function buildKeyFigure(metric: KeyReportMetric, sources: KeyFigureSources): KeyFigure {
  switch (KEY_METRIC_SOURCE[metric]) {
    case KeyFigureSource.FinanceHome: {
      const settled = settle(sources.financeHome)
      if ("figure" in settled) return settled.figure
      const home = settled.data
      if (!home.disclosed || !home.figures) return WITHHELD
      return financeHomeFigure(metric, home.figures, home.currency)
    }
    case KeyFigureSource.ChurchFinance: {
      const settled = settle(sources.churchFinance)
      if ("figure" in settled) return settled.figure
      const church = settled.data
      if (!church.disclosed || !church.figures) return WITHHELD
      return churchFigure(metric, church.figures, church.currency)
    }
    case KeyFigureSource.MembersBoard: {
      const settled = settle(sources.membersBoard)
      if ("figure" in settled) return settled.figure
      return membersFigure(settled.data)
    }
    default:
      return NONE
  }
}

function financeHomeFigure(
  metric: KeyReportMetric,
  figures: NonNullable<FinanceHomeOverview["figures"]>,
  currency: string,
): KeyFigure {
  const weeks = figures.weekly.slice(-MINI_CHART_POINTS)
  if (metric === KeyReportMetric.MonthResult) {
    const result = subtractMoney(figures.income, figures.expenses, currency)
    return {
      state: KeyFigureState.Ready,
      value: { kind: KeyValueKind.Money, amount: result, currency, signed: true },
      tone: toneOf(result),
      chart: weeks.length
        ? { kind: MiniChartKind.Pairs, pairs: weeks.map((week) => ({ income: chartNumber(week.income), expense: chartNumber(week.expense) })) }
        : null,
      context: {
        key: KeyContextKey.IncomeAndExpenses,
        money: { income: { amount: figures.income, signed: false }, expenses: { amount: figures.expenses, signed: false } },
      },
    }
  }
  if (metric === KeyReportMetric.CashOnHand) {
    return {
      state: KeyFigureState.Ready,
      value: { kind: KeyValueKind.Money, amount: figures.cashOnHand, currency, signed: false },
      tone: KeyFigureTone.Neutral,
      // Each week's earned minus spent: the movement behind today's cash, without inventing past balances.
      chart: weeks.length
        ? { kind: MiniChartKind.Bars, values: weeks.map((week) => chartNumber(week.income) - chartNumber(week.expense)) }
        : null,
      context: { key: KeyContextKey.SinceFirst, money: { amount: { amount: figures.netChange, signed: true } } },
    }
  }
  if (metric === KeyReportMetric.MonthSpending) {
    const rows = figures.spendingByCategory.slice(0, MINI_CHART_POINTS)
    return {
      state: KeyFigureState.Ready,
      value: { kind: KeyValueKind.Money, amount: figures.expenses, currency, signed: false },
      tone: KeyFigureTone.Neutral,
      chart: rows.length ? { kind: MiniChartKind.Bars, values: rows.map((row) => chartNumber(row.amount)) } : null,
      context: { key: KeyContextKey.ForIncome, money: { income: { amount: figures.income, signed: false } } },
    }
  }
  return NONE
}

function churchFigure(
  metric: KeyReportMetric,
  figures: NonNullable<ChurchFinanceOverview["figures"]>,
  currency: string,
): KeyFigure {
  if (metric === KeyReportMetric.ParishMonthResult) {
    const parish = figures.branches.find((branch) => branch.isParish)
    const months = parish?.recentNet ?? []
    const current = months[months.length - 1]
    if (!current) return NONE
    return {
      state: KeyFigureState.Ready,
      value: { kind: KeyValueKind.Money, amount: current.net, currency, signed: true },
      tone: toneOf(current.net),
      chart: months.length > 1 ? { kind: MiniChartKind.Bars, values: months.map((month) => chartNumber(month.net)) } : null,
      context: { key: KeyContextKey.ParishBooks },
    }
  }
  if (metric === KeyReportMetric.ParishCash) {
    return {
      state: KeyFigureState.Ready,
      value: { kind: KeyValueKind.Money, amount: figures.cash.atParish, currency, signed: false },
      tone: KeyFigureTone.Neutral,
      chart: null,
      context: { key: KeyContextKey.ChurchTotal, money: { total: { amount: figures.cash.total, signed: false } } },
    }
  }
  if (metric === KeyReportMetric.ChurchResult) {
    const trend = figures.cash.trend.slice(-MINI_CHART_POINTS)
    return {
      state: KeyFigureState.Ready,
      value: { kind: KeyValueKind.Money, amount: figures.cash.periodChange, currency, signed: true },
      tone: toneOf(figures.cash.periodChange),
      chart: trend.length > 1 ? { kind: MiniChartKind.Bars, values: trend.map((point) => chartNumber(point.balance)) } : null,
      context: { key: KeyContextKey.AllBranches, counts: { count: figures.branches.length } },
    }
  }
  return NONE
}

function membersFigure(board: MembersBoardOverview): KeyFigure {
  // The parish draws one bar per branch (largest first); a branch draws its month-end totals.
  const values =
    board.branches.length > 1
      ? board.branches.slice(0, MINI_CHART_POINTS).map((branch) => branch.total)
      : board.growth.slice(-MINI_CHART_POINTS).map((point) => point.total)
  const added = board.newMembers.thisMonthCount
  return {
    state: KeyFigureState.Ready,
    value: { kind: KeyValueKind.Count, count: board.counts.total },
    tone: KeyFigureTone.Neutral,
    chart: values.length > 1 && values.some((value) => value > 0) ? { kind: MiniChartKind.Bars, values } : null,
    context:
      added > 0
        ? { key: KeyContextKey.NewThisMonth, counts: { count: added } }
        : { key: KeyContextKey.ActiveMembers, counts: { count: board.counts.active } },
  }
}
